import { useEffect, useRef } from 'react';

// Auto-advance a carousel-like index on a timer. Pauses on hover,
// on `prefers-reduced-motion`, and when the tab isn't visible.
//
// Usage:
//   const hoverRef = useRef(null);
//   useAutoAdvance({
//       enabled: true,
//       interval: 5000,
//       onTick: () => setIndex((i) => (i + 1) % total),
//       hoverRef,
//   });
//   <div ref={hoverRef}>…</div>
export const useAutoAdvance = ({
    enabled = true,
    interval = 5000,
    onTick,
    hoverRef,
}) => {
    const savedTick = useRef(onTick);
    const hovered = useRef(false);

    useEffect(() => {
        savedTick.current = onTick;
    }, [onTick]);

    // Attach mouseenter/leave listeners on the hoverRef element so any
    // manual user interaction with the widget pauses the timer.
    useEffect(() => {
        const el = hoverRef && hoverRef.current;
        if (!el) return undefined;
        const onEnter = () => {
            hovered.current = true;
        };
        const onLeave = () => {
            hovered.current = false;
        };
        el.addEventListener('mouseenter', onEnter);
        el.addEventListener('mouseleave', onLeave);
        el.addEventListener('focusin', onEnter);
        el.addEventListener('focusout', onLeave);
        return () => {
            el.removeEventListener('mouseenter', onEnter);
            el.removeEventListener('mouseleave', onLeave);
            el.removeEventListener('focusin', onEnter);
            el.removeEventListener('focusout', onLeave);
        };
    }, [hoverRef]);

    useEffect(() => {
        if (!enabled) return undefined;
        // Respect users who ask for less motion.
        if (typeof window !== 'undefined') {
            const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
            if (mql.matches) return undefined;
        }

        const id = setInterval(() => {
            if (typeof document !== 'undefined' && document.hidden) return;
            if (hovered.current) return;
            savedTick.current();
        }, interval);
        return () => clearInterval(id);
    }, [enabled, interval]);
};

export default useAutoAdvance;
